'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import {
    type AccountRegisterRouteResponse,
    cartHasVetDietItem,
    cartHasVetRxItem,
    getENV,
    getNextApiRoute,
    logSentryError,
    persistAuth0ReturnTo,
    readAuth0ReturnTo,
    SentryAcpSignInErrors,
    signalAuth0CartMergeDone,
    useCart,
    useCartNotifications,
    useDecisions,
    useLocalStorage,
    usePageLayout,
} from '@libs/common-next';
import { hasAutoshipInCart } from '@libs/common-next/utils';
import type { CartUI } from '@libs/common-node';
import {
    AUTH0_CART_MERGE_COMPLETED_KEY,
    apiFetch,
    getAccessToken,
    HttpMethod,
    isAccessTokenAuth0,
    PROCEED_TO_CHECKOUT_PRESSED,
    SESSION_EVALUATED_DOM_EVENT,
    setCartCookie,
} from '@libs/core';

import { SnackbarUtils } from '../../utils';
import { getCartLineItemsSignature } from '../SignInForm/getCartLineItemsSignature';
import { type ValidateCartMergeArgs, validateCartMerge } from '../SignInForm/validateCartMerge';
import { isAuth0CheckoutLoginDestination, resolveAuth0MergePageType, runAuth0CartMerge } from './runAuth0CartMerge';

function hasCompletedAuth0CartMerge(): boolean {
    if (typeof sessionStorage === 'undefined') {
        return false;
    }
    return sessionStorage.getItem(AUTH0_CART_MERGE_COMPLETED_KEY) === '1';
}

function markAuth0CartMergeCompleted() {
    if (typeof sessionStorage === 'undefined') {
        return;
    }
    sessionStorage.setItem(AUTH0_CART_MERGE_COMPLETED_KEY, '1');
}

async function postAccountAccountsMerge(): Promise<AccountRegisterRouteResponse | null | undefined> {
    const result = await apiFetch<AccountRegisterRouteResponse>(getNextApiRoute('account/accounts'), {
        method: HttpMethod.POST,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
        credentials: 'include',
        additionalSuccessCodes: [400, 401, 404],
    });

    if (result == null || result instanceof Error || !('success' in result)) {
        return null;
    }

    return result;
}

function shouldRedirectToCheckoutAfterAuth0Merge(
    updatedCart: CartUI,
    proceedToCheckoutPressed: boolean | undefined,
    autoshipDiscountUpdatesEnabled: boolean,
    isVetSourceDietEnabled: boolean,
    clearProceedToCheckout: () => void
): boolean {
    if (!proceedToCheckoutPressed) {
        return false;
    }

    const hasAutoship =
        hasAutoshipInCart(updatedCart) && !updatedCart.hasAutoshipOrders && autoshipDiscountUpdatesEnabled;
    const hasVetRx = cartHasVetRxItem(updatedCart);
    const hasVetDiet = isVetSourceDietEnabled && cartHasVetDietItem(updatedCart);

    if (hasAutoship || hasVetRx || hasVetDiet) {
        clearProceedToCheckout();
        return true;
    }

    return false;
}

type MergeAfterAuth0TokenSyncArgs = {
    pathname: string | null;
    pageType: ReturnType<typeof usePageLayout>['pageType'];
    cart: CartUI | null;
    isCartReady: boolean;
    proceedToCheckoutPressed: boolean | undefined;
    autoshipDiscountUpdatesEnabled: boolean;
    isVetSourceDietEnabled: boolean;
    refetchCart: ValidateCartMergeArgs['refetchCart'];
    handleCartError: ValidateCartMergeArgs['handleCartError'];
    setCartNotifications: ValidateCartMergeArgs['setNotifications'];
    removeLineItemsFromCartState: ValidateCartMergeArgs['removeLineItemsFromCartState'];
    clearStockErrors: ValidateCartMergeArgs['clearStockErrors'];
    clearErrors: ValidateCartMergeArgs['clearErrors'];
    enrichCart: ValidateCartMergeArgs['enrichCart'];
    setCart: ValidateCartMergeArgs['setCart'];
    clearProceedToCheckout: () => void;
};

async function mergeAfterAuth0TokenSync({
    pathname,
    pageType,
    cart,
    isCartReady,
    proceedToCheckoutPressed,
    autoshipDiscountUpdatesEnabled,
    isVetSourceDietEnabled,
    refetchCart,
    handleCartError,
    setCartNotifications,
    removeLineItemsFromCartState,
    clearStockErrors,
    clearErrors,
    enrichCart,
    setCart,
    clearProceedToCheckout,
}: MergeAfterAuth0TokenSyncArgs) {
    const destinationPath = readAuth0ReturnTo();
    const mergePageType = resolveAuth0MergePageType(destinationPath, pageType);

    try {
        const result = await runAuth0CartMerge({
            isAuth0AccessToken: isAccessTokenAuth0(),
            accessToken: getAccessToken(),
            isCartReady,
            hasCompletedMerge: hasCompletedAuth0CartMerge(),
            pageType: mergePageType,
            isCheckoutLogin: isAuth0CheckoutLoginDestination(destinationPath, pathname),
            guestCartLineItemsSignature: getCartLineItemsSignature(cart),
            mergeGuestCart: postAccountAccountsMerge,
            refetchCart,
            validateMergedCart: mergedCart =>
                validateCartMerge({
                    mergedCart,
                    onApiFail: () => SnackbarUtils.showPredefined('SERVER_ERROR'),
                    handleCartError,
                    setNotifications: setCartNotifications,
                    removeLineItemsFromCartState,
                    refetchCart,
                    clearStockErrors,
                    clearErrors,
                    enrichCart,
                    setCart,
                }),
            setCart,
            setNotifications: setCartNotifications,
            shouldRedirectToCheckout: updatedCart =>
                shouldRedirectToCheckoutAfterAuth0Merge(
                    updatedCart,
                    proceedToCheckoutPressed,
                    autoshipDiscountUpdatesEnabled,
                    isVetSourceDietEnabled,
                    clearProceedToCheckout
                ),
            onMarkCompleted: markAuth0CartMergeCompleted,
            onSetCartCookie: setCartCookie,
            onSetPostMergePath: persistAuth0ReturnTo,
            onClearProceedToCheckout: clearProceedToCheckout,
        });

        if (result === 'done') {
            signalAuth0CartMergeDone();
        }
    } catch (error) {
        logSentryError(error, SentryAcpSignInErrors.Auth0CartMerge.MergeFailed);
        signalAuth0CartMergeDone();
    }
}

export function Auth0CartMerge() {
    const isAuth0Enabled = useDecisions('auth0');
    const pathname = usePathname();
    const { pageType } = usePageLayout();
    const {
        cart,
        isLoading,
        refetchCart,
        handleCartError,
        removeLineItemsFromCartState,
        clearStockErrors,
        clearErrors,
        enrichCart,
        setCart,
    } = useCart();
    const { setNotifications: setCartNotifications } = useCartNotifications();
    const [proceedToCheckoutPressed, setProceedToCheckoutPressed] = useLocalStorage<boolean | undefined>(
        PROCEED_TO_CHECKOUT_PRESSED
    );
    const isVetSourceDietEnabled = getENV('NEXT_PUBLIC_VET_SOURCE_DIET_ENABLED');
    const autoshipDiscountUpdatesEnabled = getENV('NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED');

    useEffect(() => {
        if (!isAuth0Enabled) {
            return;
        }

        const mergeArgs: MergeAfterAuth0TokenSyncArgs = {
            pathname,
            pageType,
            cart,
            isCartReady: !isLoading,
            proceedToCheckoutPressed,
            autoshipDiscountUpdatesEnabled,
            isVetSourceDietEnabled,
            refetchCart,
            handleCartError,
            setCartNotifications,
            removeLineItemsFromCartState,
            clearStockErrors,
            clearErrors,
            enrichCart,
            setCart,
            clearProceedToCheckout: () => setProceedToCheckoutPressed(undefined),
        };

        function onSessionEvaluated() {
            void mergeAfterAuth0TokenSync(mergeArgs);
        }

        void mergeAfterAuth0TokenSync(mergeArgs);
        globalThis.window.addEventListener(SESSION_EVALUATED_DOM_EVENT, onSessionEvaluated);

        return () => {
            globalThis.window.removeEventListener(SESSION_EVALUATED_DOM_EVENT, onSessionEvaluated);
        };
    }, [
        autoshipDiscountUpdatesEnabled,
        cart,
        isLoading,
        clearErrors,
        clearStockErrors,
        enrichCart,
        handleCartError,
        isAuth0Enabled,
        isVetSourceDietEnabled,
        pageType,
        pathname,
        proceedToCheckoutPressed,
        refetchCart,
        removeLineItemsFromCartState,
        setCart,
        setCartNotifications,
        setProceedToCheckoutPressed,
    ]);

    return null;
}
